/**
 * Product Service
 *
 * Fetches products from DynamoDB via Lambda API
 * Includes caching for performance
 */

import type { Product, RoutineCategory, PriceTier } from '@/types/product'

// API endpoint for products
const API_BASE = import.meta.env.PROD
  ? 'https://8plbeu8soh.execute-api.us-east-1.amazonaws.com/prod/api/products'
  : '/api/products' // Proxied in dev via vite.config.ts

// Cache for products (in-memory, survives page navigation but not refresh)
let productCache: Product[] | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all products from DynamoDB
 * Uses in-memory cache for performance
 */
export async function getAllProducts(forceRefresh = false): Promise<Product[]> {
  // Return cached products if available and not expired
  if (!forceRefresh && productCache && Date.now() - cacheTimestamp < CACHE_DURATION) {
    console.log('[ProductService] Returning cached products:', productCache.length)
    return productCache
  }

  console.log('[ProductService] Fetching products from API...')

  try {
    const response = await fetch(API_BASE, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.status}`)
    }

    const data = await response.json()
    const products = data.products || data

    // Update cache
    productCache = products
    cacheTimestamp = Date.now()

    console.log('[ProductService] Fetched and cached', products.length, 'products')
    return products
  } catch (error) {
    console.error('[ProductService] Error fetching products:', error)

    // Return cached products if available (even if expired) as fallback
    if (productCache) {
      console.log('[ProductService] Returning stale cache as fallback')
      return productCache
    }

    throw error
  }
}

/**
 * Get products filtered by routine category
 */
export async function getProductsByCategory(category: RoutineCategory): Promise<Product[]> {
  const allProducts = await getAllProducts()
  return allProducts.filter((p) => p.routineCategory === category)
}

/**
 * Get products filtered by price tier
 */
export async function getProductsByPriceTier(tier: PriceTier): Promise<Product[]> {
  const allProducts = await getAllProducts()
  return allProducts.filter((p) => p.priceTier === tier)
}

/**
 * Get products that work for a specific usage time
 */
export async function getProductsByUsageTime(time: 'AM' | 'PM'): Promise<Product[]> {
  const allProducts = await getAllProducts()
  return allProducts.filter((p) => p.usageTime === 'both' || p.usageTime === time)
}

/**
 * Get a single product by ID
 */
export async function getProductById(id: string): Promise<Product | null> {
  const allProducts = await getAllProducts()
  return allProducts.find((p) => p.id === id) || null
}

/**
 * Get products for a specific routine step
 * Combines category, time, and tier filtering
 */
export async function getProductsForStep(
  category: RoutineCategory,
  time: 'AM' | 'PM',
  tier?: PriceTier
): Promise<Product[]> {
  const allProducts = await getAllProducts()

  return allProducts.filter((p) => {
    // Match category
    if (p.routineCategory !== category) return false

    // Match time
    if (p.usageTime !== 'both' && p.usageTime !== time) return false

    // Match tier if specified
    if (tier && p.priceTier !== tier) return false

    return true
  })
}

/**
 * Clear the product cache
 * Call this if products are updated
 */
export function clearProductCache(): void {
  productCache = null
  cacheTimestamp = 0
  console.log('[ProductService] Cache cleared')
}

/**
 * Preload products into cache
 * Call this early in app lifecycle for better UX
 */
export async function preloadProducts(): Promise<void> {
  try {
    await getAllProducts()
    console.log('[ProductService] Products preloaded successfully')
  } catch (error) {
    console.warn('[ProductService] Failed to preload products:', error)
  }
}

// ============================================================================
// Statistics & Debug
// ============================================================================

export interface ProductStats {
  total: number
  byCategory: Record<string, number>
  byPriceTier: Record<string, number>
  byUsageTime: Record<string, number>
}

/**
 * Get product statistics for debugging
 */
export async function getProductStats(): Promise<ProductStats> {
  const products = await getAllProducts()

  const stats: ProductStats = {
    total: products.length,
    byCategory: {},
    byPriceTier: {},
    byUsageTime: {},
  }

  for (const product of products) {
    // Count by category
    stats.byCategory[product.routineCategory] = (stats.byCategory[product.routineCategory] || 0) + 1

    // Count by price tier
    stats.byPriceTier[product.priceTier] = (stats.byPriceTier[product.priceTier] || 0) + 1

    // Count by usage time
    stats.byUsageTime[product.usageTime] = (stats.byUsageTime[product.usageTime] || 0) + 1
  }

  return stats
}
